<template>
  <div>
    <DsSelect
      v-bind="attrs"
      v-model="proxy"
      :data="data"
      :fuse-options="{
        keys: ['label', 'value'],
      }"
      :required="!optional"
      :groupKey="isGrouped ? 'group' : groupKey"
      :disabled="disabled"
    />

    <div
      v-if="hint !== undefined && hint.value !== ''"
      class="text-gray-600 mt-2 text-xs"
      v-text="hint.value"
    />
  </div>
</template>

<script lang="ts" inherit-attrs="false" setup>
import { DsSelect } from '@demvsystems/design-components';
import { computed, type ComputedRef, onUnmounted, watch } from 'vue';

import { useApiChoicesStore } from '@/features/ChoicesLists/Api/store';
import type { API_CHOICE } from '@/features/ChoicesLists/Api/types';
import { useSimpleChoicesStore } from '@/features/ChoicesLists/Simple/store';
import {
  SimpleChoiceGrouped,
  SimpleChoiceMetaType,
  SimpleChoiceOption,
  SimpleChoiceType,
} from '@/features/ChoicesLists/Simple/types';
import { CHOICE_TYPE } from '@/features/ChoicesLists/types';
import { useAttrs } from '@/utils/useAttrs/useAttrs';

interface Props {
  choicesList: SimpleChoiceType | API_CHOICE;
  modelValue: string | number;
  choiceType?: CHOICE_TYPE;
  choicesId?: string;
  numeric?: boolean;
  filterContext?: ComputedRef<SimpleChoiceMetaType | null>;
  hint?: ComputedRef<string>;
  groupKey?: string;
  optional?: boolean;
  disabled?: boolean;
  isGrouped?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  choiceType: CHOICE_TYPE.SIMPLE,
  choicesId: '',
  numeric: false,
  filterContext: undefined,
  hint: undefined,
  groupKey: undefined,
  optional: false,
  disabled: false,
  isGrouped: false,
});

const attrs = useAttrs();

const emit = defineEmits(['update:modelValue']);

const simpleChoicesStore = useSimpleChoicesStore();
const apiChoicesStore = useApiChoicesStore();

const proxy = computed({
  get: () => {
    if (props.numeric && props.modelValue === 0) {
      return '';
    }

    return props.modelValue;
  },
  set: (val) => emit('update:modelValue', props.numeric ? Number(val) : val),
});

const data = computed(() => {
  if (props.choiceType === CHOICE_TYPE.SIMPLE) {
    let list = simpleChoicesStore.data[props.choicesList]?.values ?? [];

    if (props.isGrouped) {
      let groupedList: (SimpleChoiceOption & { group: string })[] = [];
      (list as unknown as SimpleChoiceGrouped[]).forEach((group) => {
        groupedList = groupedList.concat(
          group.items.map((option) => {
            return {
              ...option,
              group: group.label,
            };
          }),
        );
      });

      list = groupedList;
    }

    if (
      props.filterContext !== undefined &&
      props.filterContext.value !== null
    ) {
      const mapping = simpleChoicesStore.data[props.choicesList]?.meta.mapping;

      if (
        mapping !== undefined &&
        typeof mapping !== 'string' &&
        !Array.isArray(mapping)
      ) {
        const filteredValues = mapping[props.filterContext.value] ?? [];

        return list.filter((choice) => filteredValues.includes(choice.value));
      }
    }

    return list;
  }

  return apiChoicesStore.data[props.choicesId] ?? [];
});

const defaultValue = computed(() =>
  props.choiceType === CHOICE_TYPE.SIMPLE
    ? (simpleChoicesStore.data[props.choicesList]?.default ?? '')
    : '',
);

const isValueInData = (): boolean => {
  if (data.value.length === 0) {
    return true;
  }
  return data.value.some((listValue) => listValue.value === proxy.value);
};

const proxyEmpty = (): boolean => proxy.value === '';

const stopWatchingData = watch(
  () => data.value,
  () => {
    let proxyUpdate = false;
    if (props.choiceType === CHOICE_TYPE.API) {
      if (data.value.length === 1) {
        proxy.value = data.value[0].value;
        proxyUpdate = true;
      } else if (data.value.length === 0) {
        proxy.value = defaultValue.value;
      }
    }

    if (
      !proxyUpdate &&
      proxyEmpty() &&
      data.value.length !== 0 &&
      !isValueInData() &&
      defaultValue.value
    ) {
      proxy.value = defaultValue.value;
    }
  },
);

const stopWatchingChoices = watch(
  () => props.choicesList,
  (newChoicesList) => {
    if (
      props.choiceType === CHOICE_TYPE.SIMPLE &&
      simpleChoicesStore.data[newChoicesList] === undefined
    ) {
      simpleChoicesStore.loadChoice(newChoicesList as SimpleChoiceType);
    }
  },
  {
    immediate: true,
  },
);

const stopWatchingDefault = watch(
  () => defaultValue.value,
  (newVal) => {
    if ((proxyEmpty() && newVal) || (!isValueInData() && newVal)) {
      proxy.value = newVal;
    }
  },
  {
    immediate: true,
  },
);

const stopWatchingModelValue = watch(
  () => props.modelValue,
  (newVal) => {
    if (
      (proxyEmpty() || (props.numeric && newVal === 0)) &&
      defaultValue.value
    ) {
      proxy.value = defaultValue.value;
    }
  },
);

onUnmounted(() => {
  stopWatchingData();
  stopWatchingChoices();
  stopWatchingDefault();
  stopWatchingModelValue();
});
</script>
